/**
 * Login View
 * Super admin authentication
 */

import React, { useState, useEffect } from 'react';
import { Icons } from '../common/Icons';
import { authService } from '../../services/api';
import { consumeSessionNotice, SESSION_REASON } from '../../services/sessionExpiry';
import { canAccessOnboardingAdmin } from '../../utils/roleAccess';

const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [enroll, setEnroll] = useState(null);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [pendingSession, setPendingSession] = useState(null);

  // Session-expired banner: api.js redirects expired sessions back to
  // /admin?reason=session_expired. Read the flag, show the banner, then
  // strip the param so it doesn't persist after a successful login.
  const [sessionNotice, setSessionNotice] = useState(null);
  useEffect(() => {
    const notice = consumeSessionNotice();
    if (notice) {
      setSessionNotice(notice);
      const url = new URL(window.location.href);
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const finishLogin = (user, token) => {
    if (!canAccessOnboardingAdmin(user)) {
      setError('Access denied. Super admin privileges required.');
      setLoading(false);
      return;
    }
    onLogin(user, token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await authService.login(email, password);
      const data = response.data || response;

      if (data.requires2FASetup) {
        setEnroll(data);
        setLoading(false);
        return;
      }
      if (data.requires2FA) {
        setTwoFactorToken(data.twoFactorToken);
        setLoading(false);
        return;
      }

      finishLogin(data.user, data.token);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
      setLoading(false);
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await authService.enroll2FA(enroll.enrollToken, totpCode);
      const data = response.data || response;
      setPendingSession({ user: data.user, token: data.token });
      setRecoveryCodes(data.recoveryCodes || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await authService.verify2FA(twoFactorToken, totpCode);
      const data = response.data || response;
      finishLogin(data.user, data.token);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
      setLoading(false);
    }
  };

  if (recoveryCodes) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1 className="login-title">Save your recovery codes</h1>
          <pre style={{ textAlign: 'center', letterSpacing: '0.06em', lineHeight: 1.8 }}>
            {recoveryCodes.join('\n')}
          </pre>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: '100%', marginTop: 12 }}
            onClick={() => finishLogin(pendingSession.user, pendingSession.token)}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (enroll) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1 className="login-title">Set up authenticator</h1>
          <p className="login-subtitle">Scan the QR code, then enter the 6-digit code.</p>
          {error && <div className="login-error"><span>{error}</span></div>}
          {enroll.qrCode && (
            <img src={enroll.qrCode} alt="Authenticator QR" width={180} height={180} style={{ display: 'block', margin: '0 auto 12px' }} />
          )}
          <form onSubmit={handleEnroll}>
            <input
              className="form-control"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              autoFocus
            />
            <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 12 }} disabled={loading || totpCode.length !== 6}>
              {loading ? 'Verifying…' : 'Verify and continue'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (twoFactorToken) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1 className="login-title">Verification required</h1>
          {error && <div className="login-error"><span>{error}</span></div>}
          <form onSubmit={handleVerify2FA}>
            <input
              className="form-control"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              autoFocus
            />
            <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 12 }} disabled={loading}>
              Verify
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <Icons.Shield size={32} color="white" />
          </div>
          <h1 className="login-title">System Admin</h1>
          <p className="login-subtitle">Business Manager Administration Portal</p>
        </div>

        {sessionNotice && (
          <div
            role="status"
            style={{
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              color: '#92400e',
              padding: '10px 14px',
              borderRadius: '6px',
              marginBottom: '12px',
              fontSize: '13px',
              lineHeight: 1.45,
            }}
          >
            <strong style={{ display: 'block', marginBottom: 4 }}>
              {sessionNotice.reason === SESSION_REASON.TENANT_MISMATCH
                ? 'Workspace mismatch'
                : 'Sign in required'}
            </strong>
            {sessionNotice.message ||
              (sessionNotice.reason === SESSION_REASON.TENANT_MISMATCH
                ? 'Your previous session belonged to a different workspace. Please sign in again.'
                : 'Your session expired or is no longer valid. Please sign in again to continue.')}
          </div>
        )}

        {error && (
          <div className="login-error">
            <Icons.AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              placeholder="admin@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-control"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                style={{ paddingRight: '44px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                {showPassword ? <Icons.EyeOff size={18} /> : <Icons.Eye size={18} />}
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-lg" 
            style={{ width: '100%', marginTop: '8px' }}
            disabled={loading}
          >
            {loading ? (
              <>
                <Icons.Loader size={18} className="spin" />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <Icons.Key size={18} />
                <span>Sign In</span>
              </>
            )}
          </button>
        </form>

        <p style={{ 
          textAlign: 'center', 
          marginTop: '24px', 
          fontSize: '12px', 
          color: 'var(--text-muted)' 
        }}>
          Restricted access. Super admin credentials required.
        </p>
      </div>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LoginView;
